import * as XLSX from '@e965/xlsx';
import { auth } from '../config/firebase';
import { getCompanyProfileCached } from './cacheService';
import { PdfWorkflowMode, syncAuthTokenClaims } from './authService';
import { extractExcelFromPdf } from './pdfAttachmentService';
import { logger } from '../utils/logger';

/**
 * Settings payload as stored on the local server (canonical wire format).
 * Mirrors local-server/src/services/settingsValidator.ts.
 */
export interface RectanglePayload {
    measurementNr: number;
    x: number;
    y: number;
    width: number;
    height: number;
    toolNumber?: number | null;
    nonMeasurement?: boolean;
    toleranceUpper?: string | null;
    toleranceLower?: string | null;
    toleranceType?: 'plusminus' | 'plus' | 'minus' | null;
    nominalDimension?: string | null;
}

export interface SettingsPayload {
    revision: string;
    mdn?: string | null;
    sequenceMeasurement: boolean;
    examMode: boolean;
    refDimensions: { width: number; height: number };
    rects: RectanglePayload[];
}

export interface DetailListItem {
    detailNo: string;
    name: string | null;
    updatedAt: number;
}

export interface RevisionListItem {
    id: string;
    revision: string;
    status: 'draft' | 'published' | 'archived';
    sequenceMeasurement: boolean;
    examMode: boolean;
    createdByName: string | null;
    createdAt: number;
}

export interface RevisionDetail {
    id: string;
    detailNo: string;
    revision: string;
    status: 'draft' | 'published' | 'archived';
    sequenceMeasurement: boolean;
    examMode: boolean;
    createdByName: string | null;
    createdAt: number;
    settings: SettingsPayload;
}

export class LocalServerError extends Error {
    constructor(message: string, public readonly status: number, public readonly endpoint: string) {
        super(message);
        this.name = 'LocalServerError';
    }
}

/**
 * Resolve the local server URL for the current user from the cached company profile.
 * Returns null if not configured (caller should surface a useful error to the user).
 */
async function resolveServerUrl(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    const profile = await getCompanyProfileCached(user.uid);
    const url = profile?.localServer?.url?.trim();
    if (!url || profile?.localServer?.enabled === false) {
        return null;
    }
    return url.replace(/\/+$/, '');
}

let claimsSyncedForUid: string | null = null;

async function buildHeaders(extra?: Record<string, string>): Promise<Headers> {
    const headers = new Headers(extra);
    const user = auth.currentUser;
    if (!user) {
        throw new LocalServerError('User is not authenticated', 401, 'auth');
    }
    if (claimsSyncedForUid !== user.uid) {
        await syncAuthTokenClaims(true);
        claimsSyncedForUid = user.uid;
    }
    const token = await user.getIdToken(false);
    headers.set('Authorization', `Bearer ${token}`);
    return headers;
}

/**
 * Send a request to the local server. On 401 the token is refreshed and the call
 * is retried exactly once. Always pass a relative path starting with /api/v1/.
 */
async function request(path: string, init?: RequestInit, allowRetry = true): Promise<Response> {
    const baseUrl = await resolveServerUrl();
    if (!baseUrl) {
        throw new LocalServerError('Local server URL is not configured for your company', 503, path);
    }

    const headers = await buildHeaders(init?.headers as Record<string, string> | undefined);
    const response = await fetch(`${baseUrl}${path}`, { ...init, headers });

    if (response.status === 401 && allowRetry) {
        try {
            await auth.currentUser?.getIdToken(true);
        } catch {
            // ignore — fall through to error below
        }
        return request(path, init, false);
    }

    return response;
}

async function readJson<T>(response: Response, endpoint: string): Promise<T> {
    if (!response.ok) {
        let message = `Local server returned ${response.status}`;
        try {
            const body = await response.json() as { error?: string; message?: string };
            if (body.message) message = body.message;
            else if (body.error) message = body.error;
        } catch {
            // body is not JSON
        }
        throw new LocalServerError(message, response.status, endpoint);
    }
    return response.json() as Promise<T>;
}

export async function listDetails(query: string, limit = 20): Promise<DetailListItem[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', String(limit));
    const response = await request(`/api/v1/details?${params.toString()}`, { method: 'GET' });
    const body = await readJson<{ success: boolean; items: DetailListItem[] }>(response, 'GET /details');
    return body.items;
}

export async function listRevisions(
    detailNo: string,
    status: 'draft' | 'published' | 'archived' | null = 'published'
): Promise<RevisionListItem[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/revisions?${params.toString()}`;
    const response = await request(path, { method: 'GET' });
    const body = await readJson<{ success: boolean; items: RevisionListItem[] }>(response, path);
    return body.items.map((item) => ({
        ...item,
        examMode: item.examMode ?? false,
    }));
}

export async function getRevision(
    detailNo: string,
    revisionId: string,
    cacheBust = false
): Promise<RevisionDetail> {
    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/revisions/${encodeURIComponent(revisionId)}`;
    const url = cacheBust ? `${path}?_t=${Date.now()}` : path;
    const response = await request(url, { method: 'GET' });
    const body = await readJson<{ success: boolean; revision: RevisionDetail }>(response, path);
    const settings = normalizeSettingsPayload(body.revision.settings);
    return {
        ...body.revision,
        settings,
        sequenceMeasurement: body.revision.sequenceMeasurement ?? settings.sequenceMeasurement,
        examMode: body.revision.examMode ?? settings.examMode,
    };
}

export interface DetailWorkspaceStatus {
    registered: boolean;
}

export async function getDetailWorkspaceStatus(detailNo: string): Promise<DetailWorkspaceStatus> {
    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/workspace`;
    const response = await request(path, { method: 'GET' });
    const body = await readJson<{
        success: boolean;
        registered: boolean;
    }>(response, path);
    return { registered: body.registered };
}

export async function fetchRevisionPdf(
    detailNo: string,
    revisionId: string,
    cacheBust = false
): Promise<Blob> {
    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/revisions/${encodeURIComponent(revisionId)}/pdf`;
    const url = cacheBust ? `${path}?_t=${Date.now()}` : path;
    const response = await request(url, { method: 'GET' });
    if (!response.ok) {
        throw new LocalServerError(`Failed to download PDF (status ${response.status})`, response.status, path);
    }
    return response.blob();
}

/**
 * Build an xlsx File whose layout matches what the editor saves in PdfViewer.tsx.
 * This lets the existing PdfViewer.tsx parsing path consume server-fetched
 * revisions without any changes inside the viewer.
 */
export function buildSettingsXlsxFile(settings: SettingsPayload): File {
    const firstRowExtras: Record<string, unknown> = {
        Revision: settings.revision,
        MDN: settings.mdn || '',
        'Ref. Width': settings.refDimensions.width,
        'Ref. Height': settings.refDimensions.height,
        SequenceMeasurement: settings.sequenceMeasurement ? 'Yes' : 'No',
        ExamMode: settings.examMode ? 'Yes' : 'No'
    };

    const rows = settings.rects.map((rect, index) => {
        const base: Record<string, unknown> = {
            'M. Nr': String(rect.measurementNr),
            X: String(Math.round(rect.x)),
            Y: String(Math.round(rect.y)),
            Width: String(Math.round(Math.abs(rect.width))),
            Height: String(Math.round(Math.abs(rect.height))),
            Tool: rect.toolNumber != null ? String(rect.toolNumber) : '',
            Upper: rect.toleranceUpper ?? '',
            Lower: rect.toleranceLower ?? '',
            Type: rect.toleranceType ?? '',
            'Nominal Dim.': rect.nominalDimension ?? '',
            NonMeasurement: rect.nonMeasurement ? 'Yes' : 'No'
        };
        if (index === 0) {
            return { ...firstRowExtras, ...base };
        }
        return base;
    });

    if (rows.length === 0) {
        rows.push({ ...firstRowExtras } as Record<string, unknown>);
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Settings');
    const bytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    return new File([bytes], 'settings.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
}

function normalizeSettingsPayload(settings: SettingsPayload): SettingsPayload {
    return {
        ...settings,
        examMode: settings.examMode ?? false,
    };
}

async function applySettingsFlagsToExcelFile(
    excelFile: File,
    settings: SettingsPayload
): Promise<File> {
    const normalized = normalizeSettingsPayload(settings);
    try {
        const data = new Uint8Array(await excelFile.arrayBuffer());
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames.find(
            (name) => name.toLowerCase().includes('setup') || name.toLowerCase().includes('seadistus')
        ) || workbook.SheetNames[0];
        if (!sheetName) {
            return buildSettingsXlsxFile(normalized);
        }
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
            return buildSettingsXlsxFile(normalized);
        }
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' }) as Record<string, unknown>[];
        if (jsonData.length === 0) {
            jsonData.push({});
        }
        const firstRow = jsonData[0];
        firstRow['Revision'] = normalized.revision;
        firstRow['Ref. Width'] = normalized.refDimensions.width;
        firstRow['Ref. Height'] = normalized.refDimensions.height;
        firstRow['SequenceMeasurement'] = normalized.sequenceMeasurement ? 'Yes' : 'No';
        firstRow['ExamMode'] = normalized.examMode ? 'Yes' : 'No';
        if (normalized.mdn) {
            firstRow['MDN'] = normalized.mdn;
        }
        workbook.Sheets[sheetName] = XLSX.utils.json_to_sheet(jsonData);
        const bytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
        return new File([bytes], excelFile.name || 'settings.xlsx', {
            type: excelFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
    } catch (err) {
        logger.warn('[localServerService] Could not merge settings flags into xlsx', err);
        return buildSettingsXlsxFile(normalized);
    }
}

/**
 * Prefer embedded settings.xlsx inside the saved PDF (full editor export),
 * then overlay authoritative flags from the server settings JSON.
 */
export async function resolveSettingsExcelFile(
    pdfFile: File,
    settings: SettingsPayload
): Promise<File> {
    const normalized = normalizeSettingsPayload(settings);
    let baseFile: File;
    try {
        const embedded = await extractExcelFromPdf(pdfFile);
        baseFile = embedded ?? buildSettingsXlsxFile(normalized);
    } catch (err) {
        logger.warn('[localServerService] Could not extract embedded settings xlsx', err);
        baseFile = buildSettingsXlsxFile(normalized);
    }
    return applySettingsFlagsToExcelFile(baseFile, normalized);
}

/**
 * High-level helper for the operator path: fetches revision metadata and PDF in
 * parallel and returns ready-to-use File objects. The settings JSON is converted
 * to an xlsx File so the existing PdfViewer parser is reused unchanged.
 */
export async function fetchRevisionAsFiles(
    detailNo: string,
    revisionId: string
): Promise<{ pdfFile: File; excelFile: File; revision: RevisionDetail }> {
    const [revision, pdfBlob] = await Promise.all([
        getRevision(detailNo, revisionId, true),
        fetchRevisionPdf(detailNo, revisionId, true)
    ]);

    const safeName = `${detailNo}_${revision.revision}.pdf`.replace(/[^A-Za-z0-9._-]/g, '_');
    const pdfFile = new File([pdfBlob], safeName, { type: 'application/pdf' });
    const excelFile = await resolveSettingsExcelFile(pdfFile, revision.settings);

    logger.log('[localServerService] Loaded revision', {
        detailNo,
        revisionId,
        revision: revision.revision,
        rects: revision.settings.rects.length
    });

    return { pdfFile, excelFile, revision };
}

/**
 * Drop the per-revision metadata and PDF entries from the service-worker cache
 * (see public/sw.js: REVISIONS_CACHE). Necessary after a publish/overwrite
 * because the SW serves stale-while-revalidate; without this the editor would
 * see the previous content on the next open and only the second open would
 * pick up the freshly saved revision.
 */
async function invalidateRevisionCache(baseUrl: string, detailNo: string, revisionId: string): Promise<void> {
    if (typeof window === 'undefined' || !('caches' in window)) {
        return;
    }
    try {
        const cache = await window.caches.open('quality-datum-revisions-v1');
        const meta = `${baseUrl}/api/v1/details/${encodeURIComponent(detailNo)}/revisions/${encodeURIComponent(revisionId)}`;
        const pdf = `${meta}/pdf`;
        await Promise.all([cache.delete(meta), cache.delete(pdf)]);
    } catch (error) {
        logger.warn('[localServerService] Failed to invalidate revision SW cache', error);
    }
}

/**
 * Editor-only: publish a new revision. The PDF is uploaded as-is, the settings
 * payload is sent as a JSON string in the multipart body.
 */
export async function uploadRevision(
    detailNo: string,
    pdfBlob: Blob,
    settings: SettingsPayload,
    editorDisplayName?: string | null,
    targetRevisionId?: string | null
): Promise<RevisionListItem> {
    const form = new FormData();
    form.append('pdf', pdfBlob, `${detailNo}_${settings.revision}.pdf`);
    form.append('settings', JSON.stringify(settings));
    const trimmedName = typeof editorDisplayName === 'string' ? editorDisplayName.trim().slice(0, 200) : '';
    if (trimmedName) {
        form.append('editorDisplayName', trimmedName);
    }
    const trimmedTargetId = targetRevisionId?.trim();
    if (trimmedTargetId) {
        form.append('targetRevisionId', trimmedTargetId);
    }

    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/revisions`;
    const response = await request(path, { method: 'POST', body: form });
    const body = await readJson<{ success: boolean; revision: RevisionListItem }>(response, path);

    const baseUrl = await resolveServerUrl();
    const cacheRevisionId = trimmedTargetId || body.revision.id;
    if (baseUrl && cacheRevisionId) {
        await invalidateRevisionCache(baseUrl, detailNo, cacheRevisionId);
    }

    return {
        ...body.revision,
        examMode: body.revision.examMode ?? settings.examMode,
    };
}
/**
 * True iff the current user's company has a configured and enabled local server.
 * Used by HomePage to decide between the new (server-driven) and legacy (PDF upload) UX.
 */
export async function hasConfiguredLocalServer(): Promise<boolean> {
    const url = await resolveServerUrl();
    return url !== null;
}

/**
 * Resolve the effective PDF workflow mode from the cached company profile.
 * Falls back to legacy localServer.enabled when pdfWorkflowMode is not set.
 */
export async function getStorageMode(): Promise<PdfWorkflowMode> {
    const user = auth.currentUser;
    if (!user) return 'manual';
    const profile = await getCompanyProfileCached(user.uid);
    if (!profile) return 'manual';
    if (profile.pdfWorkflowMode) return profile.pdfWorkflowMode;
    if (profile.localServer?.enabled && profile.localServer?.url?.trim()) return 'localServer';
    return 'manual';
}

export async function hasConfiguredCloudDb(): Promise<boolean> {
    const mode = await getStorageMode();
    return mode === 'cloudDb';
}

export interface RegistrationTokenResult {
    token: string;
    expiresAt: number;
}

/**
 * Manager-only: ask the cloud backend to mint a one-time registration token.
 * The IT engineer enters this token into the local-server /setup wizard to bind
 * the on-prem server to the company. Token is single-use and expires in 24h.
 *
 * Hits POST {REACT_APP_BACKEND_URL}/api/local-server/issue-registration-token
 * authenticated with the manager's Firebase ID token. The backend enforces that
 * the caller is the company's manager (uid === companyId).
 */
export async function issueRegistrationToken(): Promise<RegistrationTokenResult> {
    const user = auth.currentUser;
    if (!user) {
        throw new LocalServerError('User is not authenticated', 401, 'issue-registration-token');
    }

    const idToken = await user.getIdToken(false);
    const backendBase = (process.env['REACT_APP_BACKEND_URL'] || 'http://localhost:3002').replace(/\/+$/, '');
    const url = backendBase.endsWith('/api')
        ? `${backendBase}/local-server/issue-registration-token`
        : `${backendBase}/api/local-server/issue-registration-token`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ companyId: user.uid })
    });

    if (!response.ok) {
        let message = `Failed to issue registration token (HTTP ${response.status})`;
        try {
            const errorBody = await response.json() as { error?: string; message?: string };
            if (errorBody.message) message = errorBody.message;
            else if (errorBody.error) message = errorBody.error;
        } catch {
            // body was not JSON
        }
        throw new LocalServerError(message, response.status, 'issue-registration-token');
    }

    const body = await response.json() as { success: boolean; token: string; expiresAt: number };
    if (!body.success || !body.token) {
        throw new LocalServerError('Backend returned no token', 502, 'issue-registration-token');
    }
    return { token: body.token, expiresAt: body.expiresAt };
}

// ============================================================================
// Operator/editor reports — saved via POST /api/v1/reports
// ============================================================================

export interface MeasurementValuePayload {
    measurementNr: number;
    value?: string | number | null;
    inSpec?: boolean | null;
    notes?: string | null;
}

export interface ReportMetadataPayload {
    detailNo: string;
    revisionId?: string | null;
    revisionLabel?: string | null;
    orderNo?: string | null;
    measurementNumber?: string | null;
    fileName: string;
    values?: MeasurementValuePayload[];
    operatorRole?: 'user' | 'editor' | 'manager';
}

export interface ReportListItem {
    id: string;
    detailNo: string;
    revisionId: string | null;
    revisionLabel: string | null;
    orderNo: string | null;
    measurementNumber: string | null;
    fileName: string;
    fileSizeBytes: number;
    operatorUid: string;
    operatorName: string | null;
    operatorEmail: string | null;
    operatorRole: string | null;
    savedAt: number;
}

export interface ReportDetail extends ReportListItem {
    values?: MeasurementValuePayload[] | null;
}

export interface ReportListFiltersInput {
    detailNo?: string;
    orderNo?: string;
    operatorUid?: string;
    /** Substring match on detail / order / operator UID / revision label (OR). When set, detailNo/orderNo ignored. */
    search?: string;
    revisionId?: string;
    /** Unix-ms timestamps */
    from?: number;
    to?: number;
    limit?: number;
    offset?: number;
}

/**
 * Upload an operator/editor report. Throws LocalServerError on failure.
 * The operator identity (uid, name, email) is NOT taken from the body but from
 * the verified Firebase ID token on the server.
 *
 * The PDF already has the measurement Excel embedded inside it (built in
 * PdfViewer), and the structured per-row data is sent as JSON in `metadata`,
 * so we deliberately do NOT ship a separate xlsx file to the server.
 */
export async function uploadOperatorReport(
    pdfBlob: Blob,
    metadata: ReportMetadataPayload
): Promise<ReportListItem> {
    const form = new FormData();
    form.append('pdf', pdfBlob, metadata.fileName);
    form.append('metadata', JSON.stringify(metadata));

    const response = await request('/api/v1/reports', { method: 'POST', body: form });
    const body = await readJson<{ success: boolean; report: ReportListItem }>(response, 'POST /reports');
    return body.report;
}

export interface SavedSettingListItem {
    revisionId: string;
    detailNo: string;
    revision: string;
    status: string;
    createdByUid: string;
    createdByName: string | null;
    createdAt: number;
    fileSizeBytes: number;
}

export interface SavedSettingsListFiltersInput {
    search?: string;
    limit?: number;
    offset?: number;
}

export async function listSavedSettings(filters: SavedSettingsListFiltersInput = {}): Promise<{
    items: SavedSettingListItem[];
    total: number;
    limit: number;
    offset: number;
}> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (typeof filters.limit === 'number') params.set('limit', String(filters.limit));
    if (typeof filters.offset === 'number') params.set('offset', String(filters.offset));
    const path = `/api/v1/saved-settings${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await request(path, { method: 'GET' });
    return readJson<{ success: boolean; items: SavedSettingListItem[]; total: number; limit: number; offset: number }>(
        response,
        path
    );
}

export async function listReports(filters: ReportListFiltersInput = {}): Promise<{
    items: ReportListItem[];
    total: number;
    limit: number;
    offset: number;
}> {
    const params = new URLSearchParams();
    if (filters.detailNo) params.set('detailNo', filters.detailNo);
    if (filters.orderNo) params.set('orderNo', filters.orderNo);
    if (filters.operatorUid) params.set('operatorUid', filters.operatorUid);
    if (filters.search) params.set('search', filters.search);
    if (filters.revisionId) params.set('revisionId', filters.revisionId);
    if (typeof filters.from === 'number') params.set('from', String(filters.from));
    if (typeof filters.to === 'number') params.set('to', String(filters.to));
    if (typeof filters.limit === 'number') params.set('limit', String(filters.limit));
    if (typeof filters.offset === 'number') params.set('offset', String(filters.offset));

    const path = `/api/v1/reports${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await request(path, { method: 'GET' });
    return readJson<{ success: boolean; items: ReportListItem[]; total: number; limit: number; offset: number }>(
        response,
        path
    );
}

export async function getReport(reportId: string): Promise<ReportDetail> {
    const path = `/api/v1/reports/${encodeURIComponent(reportId)}`;
    const response = await request(path, { method: 'GET' });
    const body = await readJson<{ success: boolean; report: ReportDetail }>(response, path);
    return body.report;
}

export async function fetchReportPdf(reportId: string): Promise<Blob> {
    const path = `/api/v1/reports/${encodeURIComponent(reportId)}/pdf`;
    const response = await request(path, { method: 'GET' });
    if (!response.ok) {
        throw new LocalServerError(`Failed to download report PDF (status ${response.status})`, response.status, path);
    }
    return response.blob();
}

export async function deleteReport(reportId: string): Promise<void> {
    const path = `/api/v1/reports/${encodeURIComponent(reportId)}`;
    const response = await request(path, { method: 'DELETE' });
    if (!response.ok) {
        let message = `Local server returned ${response.status}`;
        try {
            const body = await response.json() as { error?: string; message?: string };
            if (body.message) message = body.message;
            else if (body.error) message = body.error;
        } catch {
            // response may have empty body
        }
        throw new LocalServerError(message, response.status, path);
    }
}

export async function deleteRevision(detailNo: string, revisionId: string): Promise<void> {
    const path = `/api/v1/details/${encodeURIComponent(detailNo)}/revisions/${encodeURIComponent(revisionId)}`;
    const response = await request(path, { method: 'DELETE' });
    if (!response.ok) {
        let message = `Local server returned ${response.status}`;
        try {
            const body = await response.json() as { error?: string; message?: string };
            if (body.message) message = body.message;
            else if (body.error) message = body.error;
        } catch {
            // response may have empty body
        }
        throw new LocalServerError(message, response.status, path);
    }
}
